import { useMemo, useState } from 'react';
import { Button, Card, Input, InputNumber, DatePicker, Modal, Switch, Space, Tabs, Typography, Tag, Tooltip, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FormNode, FormColumn } from '@api/importApi';
import type { MetaAttributeDto } from '@/types/metadata';
import ReferencePicker from '@components/forms/ReferencePicker';
import BasRefPicker from '@components/forms/BasRefPicker';
import SubcontoPicker from '@components/forms/SubcontoPicker';
import EnumPicker from '@components/forms/EnumPicker';
import DocumentPicker from '@components/forms/DocumentPicker';
import { acqEntityNameToReferenceType, acqParseBasRefExternal } from '@components/forms/referenceTypes';

/** "BasEnum:VariantyXxx" → "VariantyXxx" або null. */
function acqEnumNameFromAttr(attr: MetaAttributeDto | undefined): string | null {
  if (!attr || attr.dataType !== 'enum') return null;
  const re = attr.referenceEntity;
  if (!re) return null;
  return re.startsWith('BasEnum:') ? re.substring(8) : re;
}

const { Text } = Typography;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ZERO_UUID = '00000000-0000-0000-0000-000000000000';
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

interface Props {
  nodes: FormNode[];
  data?: Record<string, unknown>;
  refMap?: Record<string, string>;
  /**
   * 'view' — read-only.
   * 'edit' — повноцінне редагування шапки + табличних частин (Tables editing,
   *          Phase 4-lite extension). UUID-references використовують ReferencePicker
   *          якщо MetaAttribute.referenceEntity мапиться у frontend-реєстр.
   */
  mode?: 'view' | 'edit';
  /** Викликається у edit-mode при будь-якій зміні (полів шапки та рядків таблиць). */
  onChange?: (acqNewData: Record<string, unknown>) => void;
  /**
   * Викликається коли змінюється КОНКРЕТНА КОМІРКА табличної частини.
   * Дає DynamicDocumentsPage достатньо контексту, щоб запустити OnRowChange handler.
   * Викликається ПІСЛЯ onChange (масив-рядки вже оновлено у data).
   */
  onCellChange?: (acqInfo: {
    tableSection: string;
    rowIndex: number;
    fieldCode: string;
    oldValue: unknown;
    newValue: unknown;
    nextData: Record<string, unknown>;
  }) => void;
  /**
   * Мапа attributeCode → MetaAttributeDto для шапки (Section='header').
   * Зберігається для зворотної сумісності + швидкого доступу.
   */
  attributesByCode?: Record<string, MetaAttributeDto>;
  /**
   * Повний масив MetaAttribute (всі секції). Потрібен для редагування табличних
   * частин — фільтрується за Section = FormNode.rowsPath.
   */
  attributes?: MetaAttributeDto[];
}

type Ctx = {
  data: Record<string, unknown>;
  refMap: Record<string, string>;
  mode: 'view' | 'edit';
  attributesByCode: Record<string, MetaAttributeDto>;
  attributes: MetaAttributeDto[];
  acqUpdate: (acqKey: string, acqValue: unknown) => void;
  acqUpdateRows: (acqRowsPath: string, acqNewRows: Record<string, unknown>[]) => void;
  /** Update single cell + (optionally) сповіщає DynamicDocumentsPage для OnRowChange engine. */
  acqUpdateCell: (acqRowsPath: string, acqRowIdx: number, acqFieldCode: string, acqOldValue: unknown, acqNewValue: unknown) => void;
};

export default function BasFormRenderer({
  nodes, data = {}, refMap = {}, mode = 'view', onChange, onCellChange, attributesByCode = {}, attributes = [],
}: Props) {
  const ctx: Ctx = {
    data,
    refMap,
    mode,
    attributesByCode,
    attributes,
    acqUpdate: (acqKey, acqValue) => {
      if (!onChange) return;
      onChange({ ...data, [acqKey]: acqValue });
    },
    acqUpdateRows: (acqRowsPath, acqNewRows) => {
      if (!onChange) return;
      onChange({ ...data, [acqRowsPath]: acqNewRows });
    },
    acqUpdateCell: (acqRowsPath, acqRowIdx, acqFieldCode, acqOldValue, acqNewValue) => {
      if (!onChange) return;
      const acqRows: Record<string, unknown>[] = Array.isArray(data[acqRowsPath])
        ? (data[acqRowsPath] as Record<string, unknown>[])
        : [];
      const acqNextRows = acqRows.map((r, i) => i === acqRowIdx ? { ...r, [acqFieldCode]: acqNewValue } : r);
      const acqNextData = { ...data, [acqRowsPath]: acqNextRows };
      onChange(acqNextData);
      if (onCellChange) {
        onCellChange({
          tableSection: acqRowsPath,
          rowIndex: acqRowIdx,
          fieldCode: acqFieldCode,
          oldValue: acqOldValue,
          newValue: acqNewValue,
          nextData: acqNextData,
        });
      }
    },
  };
  return <Space direction="vertical" style={{ width: '100%' }}>
    {nodes.map((n, i) => <RenderNode key={i} node={n} ctx={ctx} />)}
  </Space>;
}

function RenderNode({ node, ctx }: { node: FormNode; ctx: Ctx }) {
  switch (node.type) {
    case 'pages':
      return <RenderPages node={node} ctx={ctx} />;
    case 'page':
      return <RenderGroup node={node} ctx={ctx} showCard={true} />;
    case 'group':
      return <RenderGroup node={node} ctx={ctx} />;
    case 'field':
      return <RenderField node={node} ctx={ctx} />;
    case 'label':
      return <Text type="secondary" style={{ display: 'block', paddingTop: 4 }}>{node.title}</Text>;
    case 'checkbox':
      return <RenderCheckbox node={node} ctx={ctx} />;
    case 'table':
      return <RenderTable node={node} ctx={ctx} />;
    default:
      return null;
  }
}

// ───────────────────────── TABLES ─────────────────────────

function RenderTable({ node, ctx }: { node: FormNode; ctx: Ctx }) {
  const rowsPath = node.rowsPath;
  const columns = node.columns ?? [];

  // Hooks мають викликатися безумовно — інакше "Rendered fewer hooks than expected"
  const tableAttrsByCode = useMemo<Record<string, MetaAttributeDto>>(() => {
    const m: Record<string, MetaAttributeDto> = {};
    if (!rowsPath) return m;
    for (const a of ctx.attributes) {
      if (a.section === rowsPath) m[a.code] = a;
    }
    return m;
  }, [ctx.attributes, rowsPath]);

  const [acqEditRowIdx, setAcqEditRowIdx] = useState<number | null>(null);

  if (!rowsPath || columns.length === 0) return null;

  const rawRows = ctx.data[rowsPath];
  const rows: Record<string, unknown>[] = Array.isArray(rawRows)
    ? (rawRows as Record<string, unknown>[])
    : [];
  const isEdit = ctx.mode === 'edit';

  const acqUpdateCell = (acqRowIdx: number, acqFieldCode: string, acqValue: unknown) => {
    const acqOld = rows[acqRowIdx]?.[acqFieldCode];
    ctx.acqUpdateCell(rowsPath, acqRowIdx, acqFieldCode, acqOld, acqValue);
  };

  const acqAddRow = () => {
    const empty: Record<string, unknown> = {};
    for (const col of columns) {
      if (col.rowFieldCode === '_line') continue;
      const a = tableAttrsByCode[col.rowFieldCode];
      empty[col.rowFieldCode] = defaultValueForAttr(a);
    }
    ctx.acqUpdateRows(rowsPath, [...rows, empty]);
    // Одразу відкриваємо новий рядок у Modal для зручного заповнення
    setAcqEditRowIdx(rows.length);
  };

  const acqDeleteRow = (acqIdx: number) => {
    const next = rows.filter((_, i) => i !== acqIdx);
    ctx.acqUpdateRows(rowsPath, next);
  };

  const antColumns: ColumnsType<Record<string, unknown>> = columns.map((col: FormColumn) => ({
    key: col.rowFieldCode,
    title: col.title,
    width: col.width ? Math.max(col.width * 8, 80) : undefined,
    render: (_: unknown, record: Record<string, unknown>, idx: number) => {
      if (col.rowFieldCode === '_line') return idx + 1;
      const raw = record[col.rowFieldCode];
      const attr = tableAttrsByCode[col.rowFieldCode];
      if (isEdit) {
        return (
          <CellEditor
            attr={attr}
            value={raw}
            refMap={ctx.refMap}
            onChange={(v) => acqUpdateCell(idx, col.rowFieldCode, v)}
          />
        );
      }
      return renderCellReadOnly(raw, ctx.refMap);
    },
  }));

  if (isEdit) {
    antColumns.push({
      key: '__actions',
      title: '',
      width: 80,
      fixed: 'right' as const,
      render: (_: unknown, _r: Record<string, unknown>, idx: number) => (
        <Space size={2}>
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            onClick={() => setAcqEditRowIdx(idx)}
            title="Редагувати рядок у вікні"
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => acqDeleteRow(idx)}
            title="Видалити рядок"
          />
        </Space>
      ),
    });
  }

  const body = (
    <>
      {isEdit && (
        <div style={{ marginBottom: 8 }}>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={acqAddRow}
          >
            Додати рядок
          </Button>
        </div>
      )}
      <Table<Record<string, unknown>>
        size="small"
        rowKey={(_, idx) => String(idx)}
        dataSource={rows}
        columns={antColumns}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
      {isEdit && acqEditRowIdx !== null && acqEditRowIdx < rows.length && (
        <RowEditModal
          row={rows[acqEditRowIdx]}
          rowIdx={acqEditRowIdx}
          columns={columns}
          attrsByCode={tableAttrsByCode}
          refMap={ctx.refMap}
          onChange={(field, value) => acqUpdateCell(acqEditRowIdx, field, value)}
          onClose={() => setAcqEditRowIdx(null)}
        />
      )}
    </>
  );

  if (node.title) {
    return <Card size="small" title={node.title} style={{ marginBottom: 8 }}>{body}</Card>;
  }
  return <div style={{ marginBottom: 8 }}>{body}</div>;
}

/** Modal для повноцінного редагування рядка табличної частини. */
function RowEditModal({
  row, rowIdx, columns, attrsByCode, refMap, onChange, onClose,
}: {
  row: Record<string, unknown>;
  rowIdx: number;
  columns: FormColumn[];
  attrsByCode: Record<string, MetaAttributeDto>;
  refMap: Record<string, string>;
  onChange: (acqField: string, acqValue: unknown) => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={true}
      title={`Рядок №${rowIdx + 1}`}
      onCancel={onClose}
      onOk={onClose}
      okText="Готово"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={720}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {columns.filter((c) => c.rowFieldCode !== '_line').map((col) => {
          const attr = attrsByCode[col.rowFieldCode];
          const value = row[col.rowFieldCode];
          return (
            <div key={col.rowFieldCode}>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)', marginBottom: 4 }}>
                {col.title || col.rowFieldCode}
              </div>
              <CellEditor
                attr={attr}
                value={value}
                refMap={refMap}
                onChange={(v) => onChange(col.rowFieldCode, v)}
                size="middle"
              />
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function renderCellReadOnly(raw: unknown, refMap: Record<string, string>): React.ReactNode {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'boolean') return <Switch checked={raw} disabled />;
  const s = String(raw);
  if (UUID_RE.test(s) && s !== ZERO_UUID) {
    const name = refMap[s];
    if (name) return <Tooltip title={`UUID: ${s}`}><span style={{ cursor: 'help' }}>{name}</span></Tooltip>;
    return <Tag color="red" style={{ fontSize: 10 }}>unresolved</Tag>;
  }
  if (s === ZERO_UUID) return '';
  if (ISO_DATE_RE.test(s)) {
    const d = dayjs(s);
    if (d.isValid()) return d.format('DD.MM.YYYY');
  }
  return s;
}

function defaultValueForAttr(attr: MetaAttributeDto | undefined): unknown {
  if (!attr) return '';
  switch (attr.dataType) {
    case 'reference': return ZERO_UUID;
    case 'bool': return false;
    case 'decimal':
    case 'integer':
    case 'int': return 0;
    case 'date':
    case 'datetime': return null;
    default: return '';
  }
}

// ───────────────────────── CELL EDITOR ─────────────────────────

/**
 * Універсальний редактор клітинки/поля. Вибирає UI за MetaAttribute.dataType.
 * Якщо attr нема — fallback на heuristic за value (string/number/date/UUID).
 */
function CellEditor({
  attr, value, onChange, refMap, size = 'small',
}: {
  attr: MetaAttributeDto | undefined;
  value: unknown;
  onChange: (v: unknown) => void;
  refMap: Record<string, string>;
  size?: 'small' | 'middle' | 'large';
}) {
  // Reference
  const refType = attr?.dataType === 'reference'
    ? acqEntityNameToReferenceType(attr.referenceEntity)
    : null;
  const isUuid = typeof value === 'string' && UUID_RE.test(value);
  const isZero = value === ZERO_UUID;

  // Polymorphic Document reference
  if (attr?.dataType === 'reference' && attr.referenceEntity === 'Document') {
    const v = isUuid && !isZero ? (value as string) : null;
    return (
      <DocumentPicker
        acqValue={v}
        acqOnChange={(id) => onChange(id ?? ZERO_UUID)}
        acqAllowClear
        acqSize={size}
      />
    );
  }
  // Polymorphic Subconto (BAS Characteristic.* — dataType=string, referenceEntity=Subconto)
  if (attr?.referenceEntity === 'Subconto') {
    return (
      <SubcontoPicker
        acqValue={value === null || value === undefined ? null : String(value)}
        acqOnChange={(json) => onChange(json)}
        acqSize={size}
      />
    );
  }
  if (attr?.dataType === 'reference' && refType) {
    const v = isUuid && !isZero ? (value as string) : null;
    return (
      <ReferencePicker
        acqReferenceType={refType}
        acqValue={v}
        acqOnChange={(id) => onChange(id ?? ZERO_UUID)}
        acqAllowClear
        acqSize={size}
      />
    );
  }
  // Catch-all "BasRef:*" → external endpoint (довідники без AcQuantum-entity)
  const acqBasExt = attr?.dataType === 'reference' ? acqParseBasRefExternal(attr.referenceEntity) : null;
  if (acqBasExt) {
    const v = isUuid && !isZero ? (value as string) : null;
    return (
      <BasRefPicker
        acqEntityName={acqBasExt.basTypeName}
        acqTitle={acqBasExt.basTypeName}
        acqValue={v}
        acqOnChange={(id) => onChange(id ?? ZERO_UUID)}
        acqAllowClear
        acqSize={size}
      />
    );
  }
  // Reference з невідомим типом — read-only лейбл
  if (attr?.dataType === 'reference' || isUuid) {
    const display = isUuid && !isZero ? (refMap[value as string] ?? `UUID: ${(value as string).slice(0, 8)}…`) : '';
    return <Input value={display} readOnly size={size} />;
  }

  switch (attr?.dataType) {
    case 'bool':
      return <Switch size={size === 'large' ? 'default' : 'small'} checked={value === true || value === 'true'} onChange={onChange} />;
    case 'integer':
    case 'int':
      return <InputNumber
        value={typeof value === 'number' ? value : (value ? Number(value) : null)}
        onChange={(v) => onChange(v ?? 0)}
        precision={0}
        size={size}
        style={{ width: '100%' }}
      />;
    case 'decimal':
      return <InputNumber
        value={typeof value === 'number' ? value : (value ? Number(value) : null)}
        onChange={(v) => onChange(v ?? 0)}
        precision={2}
        size={size}
        style={{ width: '100%' }}
      />;
    case 'date':
    case 'datetime':
      return <DatePicker
        value={value ? dayjs(value as string) : null}
        onChange={(d) => onChange(d ? d.toISOString() : null)}
        format={attr.dataType === 'datetime' ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'}
        showTime={attr.dataType === 'datetime'}
        size={size}
        style={{ width: '100%' }}
      />;
    case 'enum': {
      const enumName = acqEnumNameFromAttr(attr);
      if (enumName) {
        return <EnumPicker
          acqEnumName={enumName}
          acqValue={value === null || value === undefined ? null : String(value)}
          acqOnChange={(v) => onChange(v)}
          acqAllowClear
          acqSize={size}
        />;
      }
      return <Input
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        size={size}
      />;
    }
    default:
      // string / unknown
      if (ISO_DATE_RE.test(String(value ?? ''))) {
        return <DatePicker
          value={value ? dayjs(value as string) : null}
          onChange={(d) => onChange(d ? d.toISOString() : null)}
          format="DD.MM.YYYY"
          size={size}
          style={{ width: '100%' }}
        />;
      }
      if (typeof value === 'number') {
        return <InputNumber
          value={value}
          onChange={(v) => onChange(v ?? 0)}
          size={size}
          style={{ width: '100%' }}
        />;
      }
      return <Input
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        size={size}
      />;
  }
}

// ───────────────────────── PAGES / GROUPS ─────────────────────────

function RenderPages({ node, ctx }: { node: FormNode; ctx: Ctx }) {
  const pages = (node.children ?? []).filter(c => c.type === 'page');
  if (pages.length === 0) return null;
  return (
    <Tabs
      style={{ marginTop: 8 }}
      items={pages.map((p, i) => ({
        key: p.name ?? String(i),
        label: p.title ?? p.name ?? `Page ${i + 1}`,
        children: (
          <Space direction="vertical" style={{ width: '100%' }}>
            {(p.children ?? []).map((c, j) => (
              <RenderNode key={j} node={c} ctx={ctx} />
            ))}
          </Space>
        ),
      }))}
    />
  );
}

function RenderGroup({ node, ctx, showCard = false }: { node: FormNode; ctx: Ctx; showCard?: boolean }) {
  const children = node.children ?? [];
  if (children.length === 0) return null;

  const inner = (
    <div style={{
      display: 'flex',
      flexDirection: node.orientation === 'horizontal' ? 'row' : 'column',
      gap: node.orientation === 'horizontal' ? 12 : 8,
      flexWrap: node.orientation === 'horizontal' ? 'wrap' : 'nowrap',
      alignItems: node.orientation === 'horizontal' ? 'flex-start' : 'stretch',
    }}>
      {children.map((c, i) => (
        <div key={i} style={{ flex: node.orientation === 'horizontal' ? '0 1 auto' : '1 1 auto' }}>
          <RenderNode node={c} ctx={ctx} />
        </div>
      ))}
    </div>
  );

  if ((showCard || node.showTitle) && node.title) {
    return <Card size="small" title={node.title} style={{ marginBottom: 8 }}>{inner}</Card>;
  }
  return inner;
}

// ───────────────────────── HEADER FIELD ─────────────────────────

function RenderField({ node, ctx }: { node: FormNode; ctx: Ctx }) {
  const acqKey = node.attributeCode;
  const rawValue = acqKey ? ctx.data[acqKey] : undefined;
  const stringValue = rawValue === null || rawValue === undefined ? '' : String(rawValue);
  const unresolved = !acqKey;

  const acqAttr = acqKey ? ctx.attributesByCode[acqKey] : undefined;
  const acqRefType = acqAttr?.dataType === 'reference'
    ? acqEntityNameToReferenceType(acqAttr.referenceEntity)
    : null;
  const acqBasExt = acqAttr?.dataType === 'reference' ? acqParseBasRefExternal(acqAttr.referenceEntity) : null;
  const isUuid = typeof rawValue === 'string' && UUID_RE.test(rawValue);
  const isZero = rawValue === ZERO_UUID;
  const resolvedName = isUuid && !isZero ? ctx.refMap[rawValue as string] : undefined;

  const acqIsRefField = isUuid || (acqAttr?.dataType === 'reference');

  // Тип спочатку з MetaAttribute, fallback — heuristic за значенням
  // (для типів додатково створених без явного dataType)
  const acqAttrType = acqAttr?.dataType;
  const acqIsNumber = typeof rawValue === 'number'
    || acqAttrType === 'integer' || acqAttrType === 'int' || acqAttrType === 'decimal';
  const acqIsDate = (typeof rawValue === 'string' && ISO_DATE_RE.test(rawValue))
    || acqAttrType === 'date' || acqAttrType === 'datetime';
  const acqIsDateTime = acqAttrType === 'datetime'
    || (typeof rawValue === 'string' && /T\d{2}:\d{2}/.test(rawValue));
  const acqIsBool = acqAttrType === 'bool' || typeof rawValue === 'boolean';

  const widthPx = node.width ? `${node.width * 12}px` : '100%';
  const acqCanPickRef = ctx.mode === 'edit' && !node.readOnly && !!acqKey && acqIsRefField && !!acqRefType;
  const acqIsEditable = ctx.mode === 'edit' && !node.readOnly && !!acqKey && !acqIsRefField;

  const acqHeader = (
    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)', marginBottom: 2 }}>
      {node.title || node.name}
      {unresolved && <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>non-header</Tag>}
      {/* unresolved-тег — лише для нетипізованих UUID без picker'а
          (типізовані рендеряться через ReferencePicker / BasExternalRefPicker, що самі резолвлять через GetById) */}
      {ctx.mode === 'view' && isUuid && !isZero && !resolvedName && !acqRefType && !acqBasExt && (
        <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>unresolved</Tag>
      )}
      {ctx.mode === 'edit' && acqIsRefField && !acqRefType && !acqBasExt && (
        <Tooltip title={`referenceEntity=${acqAttr?.referenceEntity ?? '(не задано)'} — не у frontend-реєстрі`}>
          <Tag style={{ marginLeft: 6, fontSize: 10 }}>read-only (тип не зареєстровано)</Tag>
        </Tooltip>
      )}
    </div>
  );

  // ── Polymorphic Document reference (referenceEntity = "Document")
  const acqIsDocRef = acqAttr?.dataType === 'reference' && acqAttr.referenceEntity === 'Document';
  if (acqIsDocRef) {
    const acqValue = isUuid && !isZero ? (rawValue as string) : null;
    const acqIsDocEditable = ctx.mode === 'edit' && !node.readOnly && !!acqKey;
    return (
      <div style={{ marginBottom: 4, width: widthPx }}>
        {acqHeader}
        <DocumentPicker
          acqValue={acqValue}
          acqOnChange={acqIsDocEditable ? (id) => ctx.acqUpdate(acqKey!, id ?? ZERO_UUID) : undefined}
          acqReadOnly={!acqIsDocEditable}
          acqAllowClear
        />
      </div>
    );
  }

  // ── Polymorphic Subconto (BAS Characteristic.*)
  if (acqAttr?.referenceEntity === 'Subconto') {
    const acqIsSubEditable = ctx.mode === 'edit' && !node.readOnly && !!acqKey;
    return (
      <div style={{ marginBottom: 4, width: widthPx }}>
        {acqHeader}
        <SubcontoPicker
          acqValue={rawValue === null || rawValue === undefined ? null : String(rawValue)}
          acqOnChange={acqIsSubEditable ? (json) => ctx.acqUpdate(acqKey!, json) : undefined}
          acqReadOnly={!acqIsSubEditable}
        />
      </div>
    );
  }

  if (acqCanPickRef && acqRefType) {
    const acqValue = isUuid && !isZero ? (rawValue as string) : null;
    return (
      <div style={{ marginBottom: 4, width: widthPx }}>
        {acqHeader}
        <ReferencePicker
          acqReferenceType={acqRefType}
          acqValue={acqValue}
          acqOnChange={(acqId) => ctx.acqUpdate(acqKey!, acqId ?? ZERO_UUID)}
          acqAllowClear
        />
      </div>
    );
  }

  // ── Reference у view-mode з відомим типом — ReferencePicker read-only
  // (резолвить через GET /{type}/{id} → label, бо resolveRefs працює лише для BAS-UUID)
  if (acqIsRefField && acqRefType && ctx.mode === 'view') {
    const acqValue = isUuid && !isZero ? (rawValue as string) : null;
    return (
      <div style={{ marginBottom: 4, width: widthPx }}>
        {acqHeader}
        <ReferencePicker
          acqReferenceType={acqRefType}
          acqValue={acqValue}
          acqReadOnly
        />
      </div>
    );
  }

  // ── BasRef:* (catch-all без Domain entity) → /api/bas-refs/external/{type}
  if (acqIsRefField && acqBasExt) {
    const acqValue = isUuid && !isZero ? (rawValue as string) : null;
    const acqBasEditable = ctx.mode === 'edit' && !node.readOnly && !!acqKey;
    return (
      <div style={{ marginBottom: 4, width: widthPx }}>
        {acqHeader}
        <BasRefPicker
          acqEntityName={acqBasExt.basTypeName}
          acqTitle={acqBasExt.basTypeName}
          acqValue={acqValue}
          acqOnChange={acqBasEditable ? (acqId) => ctx.acqUpdate(acqKey!, acqId ?? ZERO_UUID) : undefined}
          acqReadOnly={!acqBasEditable}
          acqAllowClear
        />
      </div>
    );
  }

  // ── Enum (Phase C) — EnumPicker (edit) / read-only (view)
  const acqEnumName = acqKey && acqAttr?.dataType === 'enum' ? acqEnumNameFromAttr(acqAttr) : null;
  if (acqEnumName) {
    return (
      <div style={{ marginBottom: 4, width: widthPx }}>
        {acqHeader}
        <EnumPicker
          acqEnumName={acqEnumName}
          acqValue={rawValue === null || rawValue === undefined ? null : String(rawValue)}
          acqOnChange={(v) => ctx.acqUpdate(acqKey!, v)}
          acqAllowClear
          acqReadOnly={ctx.mode !== 'edit' || node.readOnly}
        />
      </div>
    );
  }

  if (acqIsRefField) {
    const acqDisplay = resolvedName ?? (isZero ? '' : stringValue);
    return (
      <div style={{ marginBottom: 4 }}>
        {acqHeader}
        {resolvedName ? (
          <Tooltip title={`UUID: ${rawValue}`}>
            <Input value={acqDisplay} readOnly style={{ width: widthPx, cursor: 'help' }} />
          </Tooltip>
        ) : (
          <Input value={acqDisplay} readOnly style={{ width: widthPx }} />
        )}
      </div>
    );
  }

  // ── Date у view-mode — формат DD.MM.YYYY (інакше показується raw ISO)
  if (acqIsDate && ctx.mode === 'view') {
    const d = dayjs(rawValue as string);
    const acqDateText = d.isValid() ? d.format('DD.MM.YYYY') : stringValue;
    return (
      <div style={{ marginBottom: 4 }}>
        {acqHeader}
        <Input value={acqDateText} readOnly style={{ width: widthPx }} />
      </div>
    );
  }

  if (acqIsEditable) {
    if (acqIsBool) {
      return (
        <div style={{ marginBottom: 4 }}>
          {acqHeader}
          <Switch
            checked={rawValue === true || rawValue === 'true'}
            onChange={(v) => ctx.acqUpdate(acqKey!, v)}
          />
        </div>
      );
    }
    if (acqIsDate) {
      return (
        <div style={{ marginBottom: 4 }}>
          {acqHeader}
          <DatePicker
            value={rawValue ? dayjs(rawValue as string) : null}
            onChange={(d) => ctx.acqUpdate(acqKey!, d ? d.toISOString() : null)}
            style={{ width: widthPx }}
            format={acqIsDateTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'}
            showTime={acqIsDateTime}
          />
        </div>
      );
    }
    if (acqIsNumber) {
      return (
        <div style={{ marginBottom: 4 }}>
          {acqHeader}
          <InputNumber
            value={typeof rawValue === 'number' ? rawValue : (rawValue ? Number(rawValue) : null)}
            onChange={(v) => ctx.acqUpdate(acqKey!, v ?? 0)}
            precision={acqAttrType === 'decimal' ? 2 : 0}
            style={{ width: widthPx }}
          />
        </div>
      );
    }
    return (
      <div style={{ marginBottom: 4 }}>
        {acqHeader}
        <Input
          value={stringValue}
          onChange={(e) => ctx.acqUpdate(acqKey!, e.target.value)}
          style={{ width: widthPx }}
        />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 4 }}>
      {acqHeader}
      <Input
        value={isZero ? '' : stringValue}
        readOnly
        placeholder={unresolved ? node.dataPath : undefined}
        style={{ width: widthPx }}
      />
    </div>
  );
}

function RenderCheckbox({ node, ctx }: { node: FormNode; ctx: Ctx }) {
  const acqKey = node.attributeCode;
  const rawValue = acqKey ? ctx.data[acqKey] : undefined;
  const checked = rawValue === true || rawValue === 'true';
  const acqEditable = ctx.mode === 'edit' && !node.readOnly && !!acqKey;
  return (
    <div style={{ marginBottom: 4 }}>
      <Switch
        size="small"
        checked={checked}
        disabled={!acqEditable}
        onChange={acqEditable ? (v) => ctx.acqUpdate(acqKey!, v) : undefined}
      />
      <span style={{ marginLeft: 8 }}>{node.title || node.name}</span>
    </div>
  );
}
